import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { showSuccessToast } from '../../../../actions/notification-actions';
import {
    TARGET_COLLEGE_ADDED_TOAST_TITLE,
    buildTargetCollegeAddedMessage,
    COLLEGE_ALERTED_TOAST_TITLE,
    buildCollegeAlertedMessage,
    COLLEGE_ALERT_REMOVED_TOAST_TITLE,
    buildCollegeAlertRemovedMessage,
} from '../../CollegeCoachActivity/components/targetCollegeToast';
import { getApiAxiosInstance } from '../../../../utils/network';
import { getApiErrorMessage } from '../../../../utils/apiError';
import { targetCollegeErrorMessages } from '../targetCollegeMessages';
import { TARGET_COLLEGES_ENDPOINT, SUBSCRIPTIONS_PATH, mapResponseToRow } from '../targetCollegeList';
import {
    PowerFunnelGatedFeatures,
    CppPlayerActions,
} from '../../../Analytics/constants';
import useCollegeProspectAccess from '../../../hooks/useCollegeProspectAccess';
import useCPPTracking from '../../../hooks/useCPPTracking';
import Img from '../../../Img';
import CollegeRow from './components/CollegeRow';
import AddCollegeModal from './components/AddCollegeModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import TargetCollegeListInfoModal from './components/TargetCollegeListInfoModal';
import TargetCollegePopups from './components/TargetCollegePopups';
import UpgradeToAnnualBanner from './components/UpgradeToAnnualBanner';
import styles from '../collegeProspectTab.scss';

const TARGET_COLLEGES_PAGE_SIZE = 5;
// API max page size — fetch the whole list in one call so onCollegesLoaded can seed the
// tab-wide added-club set (grays out Add buttons in College Fit Tool / Coach Activity).
const TARGET_COLLEGES_FETCH_PAGE_SIZE = 50;
const TARGET_COLLEGES_PAGE = 1;
const TARGET_COLLEGES_FULL_LIST_PATH = '/my-profile/college-prospect/target-colleges';

const MyTargetColleges = ({ refreshKey, onCollegeAdded, onCollegeRemoved, onCollegesLoaded }) => {
    const dispatch = useDispatch();
    const { hasAnnualPower, hasPastDueSubscription, openUpgradePopup } = useCollegeProspectAccess();
    const { trackPlayerEngagement } = useCPPTracking();
    const history = useHistory();

    const [colleges, setColleges] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [alertCooldownMessage, setAlertCooldownMessage] = useState(null);
    const [addCollegeError, setAddCollegeError] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteModalError, setDeleteModalError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isListInfoOpen, setIsListInfoOpen] = useState(false);
    const [showResumePowerPopup, setShowResumePowerPopup] = useState(false);

    const gatePastDueAction = () => {
        if (hasPastDueSubscription) {
            setShowResumePowerPopup(true);
            return true;
        }
        return false;
    };

    const gateNonPowerAction = () => {
        if (!hasAnnualPower) {
            openUpgradePopup(PowerFunnelGatedFeatures.CPP_TARGET_COLLEGES_LOCKED);
            return true;
        }
        return false;
    };

    const fetchColleges = useCallback(async () => {
        setIsLoading(true);
        setFetchError(null);
        try {
            const params = {
                page: TARGET_COLLEGES_PAGE,
                pageSize: TARGET_COLLEGES_FETCH_PAGE_SIZE,
            };
            const response = await getApiAxiosInstance().get(
                TARGET_COLLEGES_ENDPOINT,
                { params },
            );
            const rows = (response.data?.results || []).map(mapResponseToRow);
            setColleges(rows);
            setTotalCount(response.data?.totalCount || 0);
            if (onCollegesLoaded) {
                onCollegesLoaded(rows.map((college) => college.id));
            }
        } catch (err) {
            setFetchError(getApiErrorMessage(err, targetCollegeErrorMessages.load));
            setColleges([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchColleges();
    }, [fetchColleges, refreshKey]);

    const hasColleges = colleges && colleges.length > 0;
    const inlineColleges = colleges.slice(0, TARGET_COLLEGES_PAGE_SIZE);
    const isViewAllEnabled = totalCount >= TARGET_COLLEGES_PAGE_SIZE;

    const handleToggleAlert = async (collegeId) => {
        if (gatePastDueAction()) {
            return;
        }
        if (gateNonPowerAction()) {
            return;
        }
        const target = colleges.find(college => college.id === collegeId);
        if (!target) {
            return;
        }
        const nextAlerted = !target.isAlerted;
        const previous = colleges;
        setColleges(colleges.map(college => (college.id === collegeId
            ? { ...college, isAlerted: nextAlerted }
            : college)));
        trackPlayerEngagement(CppPlayerActions.CPP_ALERT_COLLEGE_TOGGLED, {
            enabled: nextAlerted,
            schoolId: collegeId,
        });
        try {
            if (nextAlerted) {
                await getApiAxiosInstance().post(`${TARGET_COLLEGES_ENDPOINT}/${collegeId}/alert`);
                dispatch(showSuccessToast({
                    title: COLLEGE_ALERTED_TOAST_TITLE,
                    message: buildCollegeAlertedMessage(target.name),
                }));
            } else {
                await getApiAxiosInstance().delete(`${TARGET_COLLEGES_ENDPOINT}/${collegeId}/alert`);
                dispatch(showSuccessToast({
                    title: COLLEGE_ALERT_REMOVED_TOAST_TITLE,
                    message: buildCollegeAlertRemovedMessage(target.name),
                }));
            }
        } catch (err) {
            setColleges(previous);
            if (nextAlerted && err?.response?.status === 400) {
                setAlertCooldownMessage(getApiErrorMessage(err, targetCollegeErrorMessages.alertCooldown));
            }
        }
    };

    const handleCloseAlertCooldownPopup = () => {
        setAlertCooldownMessage(null);
    };

    const handleRequestDelete = (collegeId) => {
        if (gatePastDueAction()) {
            return;
        }
        if (gateNonPowerAction()) {
            return;
        }
        const target = colleges.find(college => college.id === collegeId);
        if (!target) {
            return;
        }
        setDeleteTarget(target);
        setDeleteModalError(null);
    };

    const handleCancelDelete = () => {
        if (isDeleting) {
            return;
        }
        setDeleteTarget(null);
        setDeleteModalError(null);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) {
            return;
        }
        setIsDeleting(true);
        setDeleteModalError(null);
        try {
            await getApiAxiosInstance().delete(`${TARGET_COLLEGES_ENDPOINT}/${deleteTarget.id}`);
            trackPlayerEngagement(CppPlayerActions.CPP_TARGET_COLLEGE_REMOVED, {
                schoolId: deleteTarget.id,
            });
            if (onCollegeRemoved) {
                onCollegeRemoved(deleteTarget.id);
            }
            setDeleteTarget(null);
            await fetchColleges();
        } catch (err) {
            setDeleteModalError(getApiErrorMessage(err, targetCollegeErrorMessages.remove));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleOpenListInfo = () => {
        setIsListInfoOpen(true);
    };

    const handleCloseListInfo = () => {
        setIsListInfoOpen(false);
    };

    const handleAddCollegeClick = () => {
        if (hasAnnualPower) {
            setIsModalOpen(true);
            return;
        }
        openUpgradePopup(PowerFunnelGatedFeatures.CPP_TARGET_COLLEGES_LOCKED);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleAddCollege = async (school) => {
        if (!school?.id) {
            return;
        }
        try {
            await getApiAxiosInstance().post(TARGET_COLLEGES_ENDPOINT, {
                clubId: school.id,
            });
            setIsModalOpen(false);
            trackPlayerEngagement(CppPlayerActions.CPP_TARGET_COLLEGE_ADDED, {
                schoolId: school.id,
            });
            dispatch(showSuccessToast({
                title: TARGET_COLLEGE_ADDED_TOAST_TITLE,
                message: buildTargetCollegeAddedMessage(school.name),
            }));
            if (onCollegeAdded) {
                onCollegeAdded(school.id);
            }
            await fetchColleges();
        } catch (err) {
            setAddCollegeError(getApiErrorMessage(err, targetCollegeErrorMessages.add));
        }
    };

    const handleCloseAddCollegeError = () => {
        setAddCollegeError(null);
    };

    return (
        <>
            <UpgradeToAnnualBanner />
            <div className={styles.collegeProspectContainer}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={styles.title} data-testid="my-target-colleges.title">My Target Colleges</div>
                        <button
                            type="button"
                            className={styles.titleInfoBtn}
                            onClick={handleOpenListInfo}
                            aria-label="About your target college list"
                            data-testid="my-target-colleges.list-info-btn"
                        >
                            <Img
                                src="/img/v4/info-icon.svg"
                                alt=""
                                className={styles.infoIcon}
                            />
                        </button>
                    </div>
                    {isViewAllEnabled ? (
                        <Link
                            to={TARGET_COLLEGES_FULL_LIST_PATH}
                            className={styles.viewAllLink}
                            data-testid="my-target-colleges.view-all-link"
                        >
                            View All
                        </Link>
                    ) : (
                        <span
                            className={`${styles.viewAllLink} ${styles.viewAllLinkDisabled}`}
                            aria-disabled="true"
                            data-testid="my-target-colleges.view-all-link"
                        >
                            View All
                        </span>
                    )}
                </div>

                <div className={styles.tableContainer}>
                    <div className={styles.tableHeader}>
                        <div className={styles.tableHeaderCell}>
                            <span className={styles.tableHeaderText} data-testid="my-target-colleges.header.name">Name</span>
                        </div>
                        <div className={styles.tableHeaderCell}>
                            <span className={styles.tableHeaderText} data-testid="my-target-colleges.header.p6">P6</span>
                        </div>
                        <div className={styles.tableHeaderCell}>
                            <span className={styles.tableHeaderText} data-testid="my-target-colleges.header.div">Division</span>
                        </div>
                        <div className={styles.tableHeaderCell}>
                            <span className={styles.tableHeaderText} data-testid="my-target-colleges.header.con">Conference</span>
                        </div>
                        <div className={styles.tableHeaderCell}>
                            <span className={styles.tableHeaderText} data-testid="my-target-colleges.header.alert-college">Alert College</span>
                        </div>
                        <div className={styles.tableHeaderCell} />
                    </div>

                    <div className={styles.tableContent}>
                        {hasColleges ? (
                            <>
                                <div className={styles.collegeRowsContainer}>
                                    {inlineColleges.map((college) => (
                                        <CollegeRow
                                            key={college.id}
                                            college={college}
                                            onToggleAlert={handleToggleAlert}
                                            onDelete={handleRequestDelete}
                                        />
                                    ))}
                                </div>
                                <div className={styles.addCollegeButtonWrapper}>
                                    <button
                                        type="button"
                                        className={`${styles.addCollegeButton} ${!hasAnnualPower ? styles.addCollegeButtonLocked : ''}`}
                                        onClick={handleAddCollegeClick}
                                        data-testid="my-target-colleges.add-college-btn"
                                    >
                                        <Img
                                            src="/img/v3/icons/icon-18px-circle-plus-teal.svg"
                                            alt="Add"
                                            className={styles.addIcon}
                                        />
                                        <span className={styles.addCollegeText} data-testid="my-target-colleges.add-college-btn.label">Add College</span>
                                        {!hasAnnualPower && (
                                            <Img
                                                src="/img/v2/icons/icon-feature-lock.svg"
                                                alt="Locked"
                                                className={styles.addCollegeLockIcon}
                                            />
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyStateMessage}>
                                    {isLoading && 'Loading your target colleges...'}
                                    {!isLoading && fetchError && fetchError}
                                    {!isLoading && !fetchError && 'Create a list of colleges that you\'re interested in attending. Once you add colleges to your list, you will have the opportunity to alert the colleges so coaches will be able find you.'}
                                </div>
                                {!isLoading && (
                                    <div className={styles.addCollegeButtonWrapper}>
                                        <button
                                            type="button"
                                            className={`${styles.addCollegeButton} ${!hasAnnualPower ? styles.addCollegeButtonLocked : ''}`}
                                            onClick={handleAddCollegeClick}
                                            data-testid="my-target-colleges.empty.add-college-btn"
                                        >
                                            <Img
                                                src="/img/v3/icons/icon-18px-circle-plus-teal.svg"
                                                alt="Add"
                                                className={styles.addIcon}
                                            />
                                            <span className={styles.addCollegeText} data-testid="my-target-colleges.empty.add-college-btn.label">Add College</span>
                                            {!hasAnnualPower && (
                                                <Img
                                                    src="/img/v2/icons/icon-feature-lock.svg"
                                                    alt="Locked"
                                                    className={styles.addCollegeLockIcon}
                                                />
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <AddCollegeModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onAddCollege={handleAddCollege}
            />
            <TargetCollegePopups
                data-testid="modal.target-colleges"
                alertCooldownMessage={alertCooldownMessage}
                onCloseAlertCooldown={handleCloseAlertCooldownPopup}
                addCollegeError={addCollegeError}
                onCloseAddCollegeError={handleCloseAddCollegeError}
                showResumePowerPopup={showResumePowerPopup}
                onResumePower={() => {
                    setShowResumePowerPopup(false);
                    history.push(SUBSCRIPTIONS_PATH);
                }}
                onCloseResumePower={() => setShowResumePowerPopup(false)}
            />
            {deleteTarget && (
                <DeleteConfirmationModal
                    collegeName={deleteTarget.name}
                    isAlerted={deleteTarget.isAlerted}
                    errorMessage={deleteModalError}
                    isDeleting={isDeleting}
                    onCancel={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                />
            )}
            {isListInfoOpen && (
                <TargetCollegeListInfoModal onClose={handleCloseListInfo} />
            )}
        </>
    );
};

MyTargetColleges.defaultProps = {
    refreshKey: 0,
    onCollegeAdded: null,
    onCollegeRemoved: null,
    onCollegesLoaded: null,
};

MyTargetColleges.propTypes = {
    refreshKey: PropTypes.number,
    onCollegeAdded: PropTypes.func,
    onCollegeRemoved: PropTypes.func,
    onCollegesLoaded: PropTypes.func,
};

export default MyTargetColleges;
